// Browser selections are guest-owned. Authenticated selections belong only to the member API.
(() => {
  const key = 'fivepoint:saved-fabrics:v1';
  const productID = /^gid:\/\/shopify\/Product\/[1-9]\d*$/;
  const fail = (code, message) => Object.assign(new Error(message), {code});
  const fingerprint = async token => [...new Uint8Array(await crypto.subtle.digest('SHA-256', Uint8Array.from(token, c => c.charCodeAt(0))))].map(n => n.toString(16).padStart(2, '0')).join('');
  class SavedClient {
    constructor({origin, storage, fetch: request = window.fetch.bind(window)}) {
      const url = new URL(origin);
      if (url.protocol !== 'https:' || url.origin !== origin) throw fail('CONFIGURATION', 'Saved fabrics are not configured.');
      this.origin = origin; this.storage = storage; this.fetch = request;
      this.ids = new Set(); this.guest = []; this.token = ''; this.listeners = new Set();
      this.state = {authenticated: null, loading: true, pending: false, count: null, error: null};
    }
    snapshot() { return {...this.state}; }
    subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
    publish(patch) { Object.assign(this.state, patch); for (const fn of this.listeners) fn(this.snapshot()); }
    readGuest() {
      let data;
      try { data = JSON.parse(this.storage.getItem(key) || '[]'); }
      catch { throw fail('STORAGE_ERROR', 'Saved fabrics could not be read in this browser.'); }
      if (!Array.isArray(data) || data.length > 200 || data.some(x => !x || !/^\/products\/[a-z0-9][a-z0-9-]{0,254}$/.test(x.url) || typeof x.title !== 'string' || x.title.length > 250)) throw fail('STORAGE_ERROR', 'The browser saved fabric list contains invalid entries.');
      return [...new Map(data.map(x => [x.url, x])).values()];
    }
    writeGuest(items) {
      try { this.storage.setItem(key, JSON.stringify(items)); }
      catch { throw fail('STORAGE_ERROR', 'Your selection could not be saved. Please allow site storage.'); }
      this.guest = items;
    }
    async request(path, {method = 'GET', body, signal} = {}) {
      const token = path === '/session' ? '' : this.token;
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 35000);
      const abort = () => controller.abort();
      if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, {once: true});
      try {
        let response;
        try {
          response = await this.fetch(this.origin + '/api/member' + path, {
            method, credentials: 'include', cache: 'no-store', signal: controller.signal,
            headers: {Accept: 'application/json', ...(token ? {'X-Member-Session': token} : {}), ...(method !== 'GET' ? {'Content-Type': 'application/json', 'X-CSRF-Token': token} : {})},
            ...(body === undefined ? {} : {body: JSON.stringify(body)})
          });
        } catch {
          throw fail(method === 'GET' ? 'NETWORK_ERROR' : 'WRITE_OUTCOME_UNKNOWN', method === 'GET' ? 'Saved fabrics could not be loaded. Please try again.' : 'This change could not be confirmed. Your browser selections have been kept. Reload before trying again.');
        }
        if (token && token !== this.token) throw fail('ACCOUNT_CHANGED', 'The account changed. Reload your saved fabrics.');
        if (response.status === 204) return;
        if (!response.headers.get('content-type')?.includes('application/json')) throw fail(method === 'GET' ? 'INVALID_RESPONSE' : 'WRITE_OUTCOME_UNKNOWN', 'The account service returned an unexpected response.');
        const data = await response.json();
        if (token && token !== this.token) throw fail('ACCOUNT_CHANGED', 'The account changed. Reload your saved fabrics.');
        if (!response.ok) throw fail(method !== 'GET' && response.status >= 500 ? 'WRITE_OUTCOME_UNKNOWN' : data.error?.code || 'REQUEST_FAILED', data.error?.message || 'Saved fabrics could not be updated.');
        return data;
      } finally {
        clearTimeout(timer); signal?.removeEventListener('abort', abort);
      }
    }
    async readState() {
      const data = await this.request('/saved-fabrics/state');
      if (!Array.isArray(data.productIds) || data.productIds.length > 200 || data.productIds.some(id => !productID.test(id))) throw fail('INVALID_RESPONSE', 'The saved fabric list was not valid.');
      this.ids = new Set(data.productIds);
      this.publish({count: this.ids.size});
    }
    refresh(options = {}) {
      if (this.refreshing) return this.refreshing;
      if (this.writing) return this.writing.then(() => this.refresh(options));
      this.refreshing = this.load(options).finally(() => { this.refreshing = null; });
      return this.refreshing;
    }
    async load({expectedFingerprint} = {}) {
      this.publish({loading: true, error: null, count: null});
      try {
        this.guest = this.readGuest();
        const session = await this.request('/session');
        if (typeof session.authenticated !== 'boolean') throw fail('INVALID_RESPONSE', 'The account session was not valid.');
        if (expectedFingerprint && (!session.authenticated || await fingerprint(session.csrfToken || '') !== expectedFingerprint)) throw fail('ACCOUNT_CHANGED', 'The account changed. Return to your account and retry.');
        this.token = session.authenticated ? session.csrfToken : '';
        this.ids.clear();
        if (session.authenticated && (typeof this.token !== 'string' || !this.token)) throw fail('INVALID_RESPONSE', 'The account session was not valid.');
        this.publish({authenticated: session.authenticated});
        if (!session.authenticated) { this.publish({count: this.guest.length}); return; }
        if (this.guest.length) {
          const importing = new Set(this.guest.map(x => x.url));
          await this.request('/saved-fabrics/import', {method: 'POST', body: {handles: [...importing].map(url => url.slice(10))}});
          // Remove only the confirmed snapshot; preserve other-tab additions and never cache account cards here.
          this.writeGuest(this.readGuest().filter(x => !importing.has(x.url)));
        }
        await this.readState();
      } catch (error) { this.publish({error, count: null}); throw error; }
      finally { this.publish({loading: false}); }
    }
    isSaved(id, url) { return this.state.authenticated ? this.ids.has(id) : this.guest.some(x => x.url === url); }
    mutate(work) {
      if (this.writing) return this.writing;
      this.publish({pending: true, error: null});
      this.writing = Promise.resolve().then(async () => {
        if (this.refreshing) await this.refreshing;
        if (this.state.authenticated === null) throw fail('NOT_READY', 'Wait for saved fabrics to load.');
        await work();
        this.publish({count: this.state.authenticated ? this.ids.size : this.guest.length});
      }).catch(error => {this.publish({error, count: null}); throw error;}).finally(() => {this.writing = null; this.publish({pending: false});});
      return this.writing;
    }
    toggle(item) {
      return this.mutate(async () => {
        if (this.state.authenticated) {
          if (!productID.test(item.productId)) throw fail('INVALID_PRODUCT', 'This fabric is missing its product identity.');
          if (this.ids.has(item.productId)) await this.removeAccount(item.productId);
          else await this.request('/saved-fabrics/import', {method: 'POST', body: {handles: [item.url.slice(10)]}});
          await this.readState();
        } else {
          const items = this.readGuest(), found = items.some(x => x.url === item.url);
          if (!found && items.length >= 200) throw fail('SAVED_LIMIT', 'Your saved list can contain up to 200 fabrics.');
          this.writeGuest(found ? items.filter(x => x.url !== item.url) : [...items, {url: item.url, title: item.title, image: item.image}]);
        }
      });
    }
    removeAccount(id) {
      if (!productID.test(id)) throw fail('INVALID_PRODUCT', 'This fabric is missing its product identity.');
      const opaque = btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      return this.request('/saved-fabrics/' + opaque, {method: 'DELETE'});
    }
    remove(id, url) {
      return this.mutate(async () => {
        if (this.state.authenticated) { await this.removeAccount(id); await this.readState(); }
        else this.writeGuest(this.readGuest().filter(x => x.url !== url));
      });
    }
    async list(cursor, signal) {
      if (this.refreshing) await this.refreshing;
      if (this.state.error) throw this.state.error;
      if (this.state.authenticated) return this.request('/saved-fabrics' + (cursor ? '?cursor=' + encodeURIComponent(cursor) : ''), {signal});
      if (this.state.authenticated === null) throw fail('NOT_READY', 'Wait for saved fabrics to load.');
      return {nodes: this.guest.map(x => ({...x, image: x.image ? {url: x.image} : null})), nextCursor: null};
    }
  }
  window.FivePointSaved = {create: options => new SavedClient(options), fingerprint};
})();
